import React, { useCallback, useMemo } from 'react';
import { useExpanded, useFlexLayout, useRowSelect, useTable } from 'react-table';
import { useIntl, FormattedMessage } from 'react-intl';

import { FlexColumn } from 'ui-components/components/flex';
import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import EmptyBoxIcon from '../../../../../../svgs/empty-box.svg';
import { columns, AddOnDescriptionColumnId, AddOnNameColumnId, AddOnPriceColumnId, AddOnStatusColumnId } from './columns';
import { useManageSubscriptionDrawerContext } from '../../../context/manage-subscription-drawer-context';
import { AddOns } from '../../../../../../constants/add-ons';

import style from './manage-add-ons.module.css';
import { messages } from './messages';

export const ManageAddOns = () => {
  const intl = useIntl();
  const { visibleLineItems, setAddOnsToRemove, setAddOnsToAdd, addOnsToRemove, addOnsToAdd } = useManageSubscriptionDrawerContext();

  const shouldDisplayEmptyLineItemsPlaceholder = !visibleLineItems || visibleLineItems.length === 0;

  const onAddAddOn = useCallback(
    (code) => {
      setAddOnsToAdd((prev) => [...prev, code]);
      setAddOnsToRemove((prev) => prev.filter((a) => a !== code));
    },
    [setAddOnsToAdd, setAddOnsToRemove],
  );

  const onRemoveAddOn = useCallback(
    (code) => {
      setAddOnsToAdd((prev) => prev.filter((a) => a !== code));
      setAddOnsToRemove((prev) => [...prev, code]);
    },
    [setAddOnsToAdd, setAddOnsToRemove],
  );

  const items = useMemo(() => {
    const addOns = visibleLineItems
      // Mesh is filtered out here because we don't want it in the manage subscription drawer but do want it on the payment page subscription card
      // and filterLineItemsByFeatureUniqueness is a shared function
      .filter((lineItem) => {
        const lineItemCode = lineItem.lineItemCode;
        return lineItemCode !== AddOns.DropShip && lineItemCode !== AddOns.FulfillmentProviderBundle;
      })
      .map((lineItem) => {
        return {
          id: lineItem.lineItemCode,
          [AddOnNameColumnId]: {
            displayName: intl.formatMessage(lineItem.nameMessageDescriptor),
            isTrial: lineItem.isTrial,
            isAlphaUser: lineItem.isAlphaUser,
            isIncludedInPlan: lineItem.isIncludedInPlan,
            deactivationDateTimeUtc: lineItem.deactivationDateTimeUtc,
          },
          [AddOnPriceColumnId]: lineItem.price,
          [AddOnDescriptionColumnId]: intl.formatMessage(lineItem.descriptionMessageDescriptor),
          [AddOnStatusColumnId]: {
            lineItem,
            isMarkedForPurchase: addOnsToAdd.some((addOn) => addOn === lineItem.addOnCode),
            isMarkedForRemoval: addOnsToRemove.some((addOn) => addOn === lineItem.addOnCode),
            onAddAddOn: () => onAddAddOn(lineItem.lineItemCode),
            onRemoveAddOn: () => onRemoveAddOn(lineItem.lineItemCode),
          },
        };
      });
    return [...addOns];
  }, [visibleLineItems, intl, addOnsToAdd, addOnsToRemove, onAddAddOn, onRemoveAddOn]);

  const { getTableProps, headerGroups, getTableBodyProps, rows, prepareRow } = useTable(
    {
      columns,
      data: items,
      autoResetSelectedRows: false,
      autoResetExpanded: false,
    },
    useFlexLayout,
    useExpanded,
    useRowSelect,
  );

  if (shouldDisplayEmptyLineItemsPlaceholder) {
    return (
      <FlexColumn className={style['empty-box-wrapper']}>
        <img alt='empty box' className={style['empty-box']} src={EmptyBoxIcon} />
        <Text className={style['empty-header']} textType={TextType.Label} value={<FormattedMessage {...messages.noActiveAddOnsHeader} />} />
        <Text
          className={style['empty-note']}
          textType={TextType.Paragraph}
          value={<FormattedMessage {...messages.noActiveAddOnsDescription} />}
        />
      </FlexColumn>
    );
  }

  return (
    <>
      <FlexColumn className={style['manage-add-ons-body']}>
        <Text className={style['manage-add-ons-description']} textType={TextType.Paragraph} value={messages.addOnsDescription} />
        <ReactTableWrapper
          className={style['manage-add-ons-grid']}
          getBodyRowClassName={() => style['manage-add-ons-body-row']}
          getTableBodyProps={getTableBodyProps}
          getTableProps={getTableProps}
          headerGroups={headerGroups}
          headerRowClassName={style['header-row']}
          prepareRow={prepareRow}
          rows={rows}
        />
      </FlexColumn>
    </>
  );
};
